/**
 * MCP Resource
 *
 * Thin 1:1 mapping to the MCP and tools API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  MCPServer,
  AppRegisterMCPRequest,
  AppUpdateMCPRequest,
  MCPTool,
  MCPToolCatalog,
} from '../types/index.js';

export class MCPResource {
  constructor(private http: HttpLayer) {}

  // --- Device-scoped MCP endpoints ---

  async register(
    deviceId: string,
    params: AppRegisterMCPRequest,
  ): Promise<MCPServer> {
    return this.http.post(
      `/v1/devices/${deviceId}/mcp`,
      params,
    );
  }

  async list(deviceId: string): Promise<MCPServer[]> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp`,
    );
  }

  async update(
    deviceId: string,
    mcpId: string,
    params: AppUpdateMCPRequest,
  ): Promise<MCPServer> {
    return this.http.put(
      `/v1/devices/${deviceId}/mcp/${mcpId}`,
      params,
    );
  }

  async delete(
    deviceId: string,
    mcpId: string,
  ): Promise<MCPServer> {
    return this.http.delete(
      `/v1/devices/${deviceId}/mcp/${mcpId}`,
    );
  }

  async getToolCatalog(deviceId: string): Promise<MCPToolCatalog> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp/catalog`,
    );
  }

  async listTools(
    deviceId: string,
    mcpId: string,
  ): Promise<MCPTool[]> {
    return this.http.get(
      `/v1/devices/${deviceId}/mcp/${mcpId}/tools`,
    );
  }

  // --- Tenant-scoped tool endpoints ---

  async listTenantMCPs(): Promise<MCPServer[]> {
    return this.http.get('/v1/tools/mcps');
  }

  async listTenantMCPTools(mcpId: string): Promise<MCPTool[]> {
    return this.http.get(`/v1/tools/mcps/${mcpId}/tools`);
  }

  async getTenantToolCatalog(): Promise<MCPToolCatalog> {
    return this.http.get('/v1/tools/catalog');
  }
}
